import { useCallback, useMemo, useState } from 'react';

import { buildInitialFormState, isFormValid } from '../utils';
import type {
  KeyValueEntry,
  MetadataEditFormState,
  StringEntry,
} from '../types';

export type EditMetadataFormController = {
  state: MetadataEditFormState;
  valid: boolean;
  formInstanceId: number;
  updateField: <K extends keyof MetadataEditFormState>(
    field: K,
    value: MetadataEditFormState[K]
  ) => void;
  setSttKeywords: (entries: StringEntry[]) => void;
  setCustomFields: (entries: KeyValueEntry[]) => void;
  reset: (metadata?: string | Record<string, unknown> | null) => void;
};

export const useEditMetadataForm = (
  initialMetadata?: string | Record<string, unknown> | null
): EditMetadataFormController => {
  const [state, setState] = useState<MetadataEditFormState>(() =>
    buildInitialFormState(initialMetadata)
  );
  const [formInstanceId, setFormInstanceId] = useState(0);

  const updateField = useCallback(
    <K extends keyof MetadataEditFormState>(
      field: K,
      value: MetadataEditFormState[K]
    ) => {
      setState(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const setSttKeywords = useCallback(
    (entries: StringEntry[]) => updateField('stt_keywords', entries),
    [updateField]
  );

  const setCustomFields = useCallback(
    (entries: KeyValueEntry[]) => updateField('custom_fields', entries),
    [updateField]
  );

  const reset = useCallback(
    (metadata?: string | Record<string, unknown> | null) => {
      setState(buildInitialFormState(metadata));
      setFormInstanceId(id => id + 1);
    },
    []
  );

  const valid = useMemo(() => isFormValid(state), [state]);

  return {
    state,
    valid,
    formInstanceId,
    updateField,
    setSttKeywords,
    setCustomFields,
    reset,
  };
};
